import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { SubContentProps } from '../../LanguageSetting';
import { validateFile } from '@/components/file-thumbnail/utils';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import useUpdateProfileImage from '@/hooks/apiHooks/useUpdateProfileImage';
import { EditProfileProps } from '../index';
import useProfileForm, { ProfileForm } from './useProfileForm';
import {
  checkNickname,
  updateNickname,
  useChangedJob,
  useGetJobList,
  usePassport,
} from '@/hooks/apiHooks';
import useMutateMyInfo from '@/hooks/apiHooks/useUpdateProfileData';
import { useTranslation } from 'react-i18next';

const useEditProfileProps = (settings: SubContentProps): EditProfileProps => {
  const { t } = useTranslation();
  const {
    myInfo: { profileImageUrl, nickname = '', job, passportId },
  } = settings;

  const dialog = useDialog();
  const { showSuccess } = useSnackbar();

  const { update: updateMyInfo } = useMutateMyInfo(passportId);
  const { mutate: updatePassportData } = usePassport();

  const { isLoading: isJobListFetching, data: jobList } = useGetJobList();

  const methods = useProfileForm();
  const {
    handleSubmit,
    getFieldState,
    setValue,
    getValues,
    setError,
    clearErrors,
    reset,
    formState: { isValid, isDirty, errors },
    trigger,
  } = methods;

  const { update, isLoading: isEditButtonDisabled } = useUpdateProfileImage();

  const [isNickNameAvailable, setIsNickNameAvailable] = useState(false);
  const [isNickNameChecking, setIsNickNameChecking] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const isFormValid =
    getValues('nickname').trim().length === 0 ||
    isNickNameAvailable ||
    (isDirty && isValid);

  const resetLocalState = () => {
    setIsNickNameAvailable(false);
    setIsNickNameChecking(false);
    setIsFormSubmitting(false);
  };

  const handleNicknameOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsNickNameAvailable(false);
    setValue('nickname', e.target.value.trim().replace(/[^a-zA-Z\s]/g, ''), {
      shouldValidate: true,
    });
  };

  const nicknameOnClear = () => {
    setIsNickNameAvailable(false);
    setValue('nickname', '', {
      shouldValidate: true,
    });
  };

  const editProfileImage = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      validateFile([file], {
        isOnlyImage: true,
        maxLength: 1,
      });
    } catch (e) {
      if (e instanceof Error) {
        dialog.show({
          title: t('MYPAGE.이미지를 다시 선택해주세요.'),
          message: e?.message,
        });
      } else {
        dialog.show({ message: t('에러가 발생했습니다.') });
      }
    }

    try {
      await update(file);
      await updatePassportData();
    } catch (e) {
      dialog.show({ message: t('MYPAGE.이미지를 변경에 실패했습니다.') });
    }
  };

  const handleFormOnSubmit = (e: FormEvent) => {
    e.preventDefault();

    handleSubmit(async (data: ProfileForm) => {
      try {
        setIsFormSubmitting(true);

        const { nickname, job, summary, content } = data;
        if (settings.myInfo.nickname !== nickname) {
          await updateNickname(passportId, nickname);
        }

        if (
          settings.myInfo.job.name !== job ||
          settings.myInfo.job.summary !== summary ||
          settings.myInfo.job.content !== content
        ) {
          await useChangedJob(data);
        }

        //TODO: 업데이트 바로 되도록 수정
        await updateMyInfo({
          ...settings.myInfo,
          nickname,
          job: {
            name: job,
            summary: summary,
            content: content,
          },
        });

        resetLocalState();
        showSuccess({ message: t('MYPAGE.프로필이 변경되었습니다.') });
      } catch (e: any) {
        console.error(e);
        dialog.show({ message: t('MYPAGE.정보 수정에 변경에 실패했습니다.') });
      } finally {
        setIsFormSubmitting(false);
      }
    })();
  };

  const checkDuplicateName = async () => {
    const nicknameKey = 'nickname';

    setIsNickNameChecking(true);
    clearErrors(nicknameKey);

    try {
      await checkNickname(getValues(nicknameKey));
      setIsNickNameAvailable(true);
    } catch (e) {
      setError(nicknameKey, {
        type: 'manual',
        message: t('MYPAGE.이미 사용중인 이름이에요.'),
      });
      setIsNickNameAvailable(false);
    } finally {
      setIsNickNameChecking(false);
    }
  };

  useEffect(() => {
    reset({
      nickname: (nickname || '').replace(/[^a-zA-Z]/g, ''),
      job: job.name || '',
      summary: job.summary || '',
      content: job.content || '',
    });

    trigger();
  }, [job, nickname, reset, trigger]);

  return {
    nicknameHelperText:
      errors.nickname?.message ??
      (isNickNameAvailable
        ? t('MYPAGE.사용 가능한 이름이에요.')
        : t('MYPAGE.최대 {{n}}자까지 가능합니다.', { n: 15 })),
    nicknameHelperTextColor: isNickNameAvailable
      ? '#118D57'
      : 'var(--dk-text-secondary, #888D96)',
    nicknameHasError: !!errors.nickname,
    nicknameOnChange: handleNicknameOnChange,
    nicknameOnClear,
    editProfileImage,
    isEditButtonDisabled,
    profileImageUrl,
    formProps: { methods, onSubmit: handleFormOnSubmit, id: 'mypage-profile' },

    checkDuplicateName,
    isCheckButtonDisabled:
      getValues('nickname') === '' ||
      getFieldState('nickname').invalid ||
      getValues('nickname') === nickname ||
      isNickNameAvailable,
    isCheckButtonLoading: isNickNameChecking,

    isSubmitButtonDisabled: !isFormValid,
    isFormSubmitting,

    jobOptions: useMemo(
      () =>
        jobList?.data?.map((item) => {
          return {
            label: `${item.name_kor}`,
            value: `${item.name_kor}`,
          };
        }) ?? [],
      [jobList],
    ),
    jobValue: getValues('job'),
    isJobListFetching,
  };
};

export default useEditProfileProps;
